// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState, VideoDocumentModel } from "@steiner/App/Store/Reducers";
import { getProductVideos } from "@steiner/App/Store/Selectors/ProductDetailsSelector";
import { getYoutubeThumbnail, parseBoolean } from "@steiner/XDL/Utilities/helpers";
import useEmblaCarousel from "embla-carousel-react";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    scrollingBehavior?: string;
    numberOfVisibleThumbnails?: number;
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
    // XNG-Changes
    selectedVideoIndex?: number;
    onSelectVideo?: (index: number) => void;
}

// XNG-Change
// parse ownProps to the mapStateToProps and use the CustomAppState
const mapStateToProps = (state: CustomAppState, ownProps: HasProductContext) => ({
    productSettings: getSettingsCollection(state).productSettings,
    // XNG-Change
    videos: getProductVideos(state, ownProps.productContext?.product),
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
    verticalPrevArrowGridItem?: GridItemProps;
    verticalCarouselGridItem?: GridItemProps;
    verticalNextArrowGridItem?: GridItemProps;
    // XNG-Change
    illustrationMessage?: TypographyPresentationProps;
    carouselOuterWrapper?: InjectableCss;
    videoIcon?: IconPresentationProps;
}

// XNG-Changes
// make borderColor is optional and change style
const getImageCss = (borderColor: string) => css`
    width: 100%;
    height: 100%;
    max-width: 52px;
    max-height: 52px;
    border: 1px solid ${getColor(borderColor)};
    cursor: pointer;
    justify-content: flex-start;
    img {
        max-width: 52px;
        max-height: 52px;
        aspect-ratio: 1;
    }
`;

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    mainContainer: { gap: 0 },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
        `,
    },
    verticalPrevArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: { width: 10 },
    verticalCarouselGridItem: { width: 12 },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            padding: 10px 3px 0;
            display: flex;
            justify-content: center;
        `,
    },
    image: {
        css: getImageCss("common.white"),
    },
    selectedImage: {
        css: getImageCss("common.ui13"),
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
        `,
    },
    verticalNextArrowGridItem: {
        width: 12,
        align: "middle",
        css: css`
            justify-content: center;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
        `,
    },
};

export const CarouselSlidesContainer = styled.div<
    InjectableCss & { isVertical: boolean; numberOfVisibleThumbnails: number }
>`
    display: flex;
    flex-direction: ${props => (props.isVertical ? "column" : "row")};
    align-items: ${props => (props.isVertical ? "center" : "initial")};
    ${props => (props.isVertical ? `height: ${props.numberOfVisibleThumbnails * 74}px;` : "")}
    ${injectCss};
`;

export const CarouselSlide = styled.div<InjectableCss & { numberOfVisibleThumbnails: number }>`
    flex: 0 0 ${props => 100 / props.numberOfVisibleThumbnails - 4}%;
    padding: 0;
    position: relative;
    &:first-child > div {
        padding-left: 0;
    }
    ${injectCss};
`;

const ProductImageCarousel = ({
    productSettings,
    productContext,
    scrollingBehavior,
    numberOfVisibleThumbnails = 6,
    selectedIndex,
    onSelectImage,
    extendedStyles,
    // XNG-Changes
    selectedVideoIndex,
    onSelectVideo,
    videos,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;

    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };

    const isVertical = scrollingBehavior === "vertical";
    const [emblaRef, embla] = useEmblaCarousel({
        axis: isVertical ? "y" : "x",
    });
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const updateVisibility = () => {
        if (!embla || !product.images) {
            return;
        }

        const canScroll = embla.canScrollPrev() || embla.canScrollNext();
        if (!canScroll) {
            embla.slideNodes().forEach(slide => {
                slide.setAttribute("data-is-visible", "true");
            });
            return;
        }

        const viewport = embla.rootNode().getBoundingClientRect();
        const buffer = 5;

        embla.slideNodes().forEach((slide, index) => {
            const slideRect = slide.getBoundingClientRect();
            const isFullyVisible =
                slideRect.top >= viewport.top - buffer &&
                slideRect.bottom <= viewport.bottom + buffer &&
                slideRect.left >= viewport.left - buffer &&
                slideRect.right <= viewport.right + buffer;

            slide.setAttribute("data-is-visible", isFullyVisible ? "true" : "false");
        });
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        const handleUpdate = () => {
            setCanScroll();
            updateVisibility();
        };

        embla.on("init", handleUpdate);
        embla.on("select", handleUpdate);
        embla.on("reInit", handleUpdate);
        embla.on("settle", handleUpdate);

        handleUpdate();
    }, [embla]);

    useEffect(() => {
        if (!embla || !product.images) {
            return;
        }

        embla.reInit({
            align: product.images.length > numberOfVisibleThumbnails || isVertical ? "start" : "center",
            slidesToScroll: numberOfVisibleThumbnails,
            axis: isVertical ? "y" : "x",
        });

        updateVisibility();
    }, [embla, product, scrollingBehavior, numberOfVisibleThumbnails]);

    const getProductImageThumbPath = (productImage: ImageModel) => {
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.includes("?") ? "&thumb" : "?thumb");
        }
        return "";
    };

    if (!product || !product.images || product.images.length === 0) {
        return null;
    }

    const videoClickHandler = (index: number) => {
        onSelectVideo?.(index);
    };

    const renderVideos = (productVideo: VideoDocumentModel, index: number) => {
        return (
            <CarouselSlide
                key={productVideo.id}
                numberOfVisibleThumbnails={numberOfVisibleThumbnails}
                data-is-selected={index === selectedImageIndex}
                data-is-visible="false"
            >
                <StyledWrapper {...styles.carouselSlideInner}>
                    <Clickable onClick={() => videoClickHandler(index)} aria-label="product video clickable">
                        <LazyImage
                            {...styles.image}
                            {...{ selected: index === selectedVideoIndex }}
                            src={getYoutubeThumbnail(productVideo.videoLink)}
                            altText={productVideo.filename}
                        />
                        <Icon {...styles.videoIcon} />
                    </Clickable>
                </StyledWrapper>
            </CarouselSlide>
        );
    };

    // XNG-Change
    // Change Structure
    return (
        <StyledWrapper {...styles.mainContainer} key={product.id}>
            {/* XNG-Changes Add custom site message */}
            <Typography {...styles.illustrationMessage}>{siteMessage("Custom_Product_Images_Illustration")}</Typography>
            <StyledWrapper {...styles.carouselOuterWrapper}>
                {/* XNG-Changes */}
                {/* Move canScrollPrev from disabled to hide the arrow completely */}
                {canScrollPrev && (
                    <StyledWrapper {...(isVertical ? styles.verticalPrevArrowGridItem : styles.prevArrowGridItem)}>
                        {product.images.length > numberOfVisibleThumbnails && (
                            <Button
                                {...styles.prevArrowButton}
                                onClick={() => embla && embla.scrollPrev()}
                                // disabled={!canScrollPrev}
                                aria-label={translate("Previous")}
                                data-test-selector="prevBtn"
                            >
                                <ButtonIcon src={isVertical ? ChevronUp : ChevronLeft} />
                            </Button>
                        )}
                    </StyledWrapper>
                )}
                <StyledWrapper {...(isVertical ? styles.verticalCarouselGridItem : styles.carouselGridItem)}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <CarouselSlidesContainer
                                isVertical={isVertical}
                                numberOfVisibleThumbnails={numberOfVisibleThumbnails}
                                data-test-selector="slides"
                                data-test-selector-isVertical={isVertical}
                            >
                                {product.images.map((productImage, index) => (
                                    <CarouselSlide
                                        key={productImage.id}
                                        numberOfVisibleThumbnails={numberOfVisibleThumbnails}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            data-test-selector="carousel_slide"
                                        >
                                            <Clickable onClick={() => imageClickHandler(index)}>
                                                <LazyImage
                                                    {...(index === selectedImageIndex
                                                        ? styles.selectedImage
                                                        : styles.image)}
                                                    src={getProductImageThumbPath(productImage)}
                                                    altText={productImage.imageAltText}
                                                />
                                            </Clickable>
                                        </StyledWrapper>
                                    </CarouselSlide>
                                ))}
                                {/* XNG-Change: Show videos */}
                                {videos
                                    ?.filter(video => parseBoolean(video.isYoutubeVideo))
                                    .map((productVideo: VideoDocumentModel, index: number) =>
                                        renderVideos(productVideo, index),
                                    )}
                            </CarouselSlidesContainer>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                {/* XNG-Changes */}
                {/* Move canScrollNext from disabled to hide the arrow completely */}
                {canScrollNext && (
                    <StyledWrapper {...(isVertical ? styles.verticalNextArrowGridItem : styles.nextArrowGridItem)}>
                        {product.images.length > numberOfVisibleThumbnails && (
                            <Button
                                {...styles.nextArrowButton}
                                onClick={() => embla && embla.scrollNext()}
                                // disabled={!canScrollNext}
                                aria-label={translate("Next")}
                                data-test-selector="nextBtn"
                            >
                                <ButtonIcon src={isVertical ? ChevronDown : ChevronRight} />
                            </Button>
                        )}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductImageCarousel));
